import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "../ui";
import { useStravaStatus } from "../../hooks/useStravaStatus";
import { useStravaActivities } from "../../hooks/useStravaActivities";
import { StravaConnectButton, StravaIcon } from "./StravaConnectButton";
import { StravaActivityCard } from "./StravaActivityCard";
import { AlertCircle, RefreshCw, Sparkles, CheckCircle2 } from "lucide-react";

export function StravaImportModal({
  isOpen,
  onClose,
  idCorrida,
  idCategoriaCorrida,
  onSuccess,
  onError,
}) {
  const {
    isConnected,
    loading: statusLoading,
    isConnecting,
    connect,
  } = useStravaStatus();

  const {
    activities,
    loading: activitiesLoading,
    importing,
    error: activitiesError,
    fetchActivities,
    importActivity,
  } = useStravaActivities();

  const [selectedActivity, setSelectedActivity] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (isOpen && isConnected) {
      setSelectedActivity(null);
      setSuccessMessage(null);
      fetchActivities();
    }
  }, [isOpen, isConnected, fetchActivities]);

  const handleConnect = async () => {
    try {
      await connect({
        onSuccess: () => {
          fetchActivities();
        },
      });
    } catch (err) {
      console.error("Falha ao conectar:", err);
    }
  };

  const handleConfirmImport = async () => {
    if (!selectedActivity || !idCorrida) return;

    try {
      const payload = {
        activity_id: String(selectedActivity.id),
        id_corrida: idCorrida,
        ...(idCategoriaCorrida ? { id_categoria_corrida: idCategoriaCorrida } : {}),
      };

      const result = await importActivity(payload);
      setSuccessMessage("Métricas importadas do Strava com sucesso!");

      setTimeout(() => {
        if (onSuccess) onSuccess(result);
        onClose();
      }, 1200);
    } catch (err) {
      console.error(err);
      if (onError) onError(err);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Importar Dados do Strava"
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4">
        {/* Caso 1: Carregando status da conexão */}
        {statusLoading ? (
          <div className="py-12 flex flex-col items-center justify-center gap-3 text-slate-400">
            <Spinner size="md" />
            <p className="text-sm">Verificando conexão com o Strava...</p>
          </div>
        ) : !isConnected ? (
          /* Caso 2: Não conectado ao Strava */
          <div className="py-8 px-4 text-center flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#FC4C02]/15 border border-[#FC4C02]/30 flex items-center justify-center text-[#FC4C02]">
              <StravaIcon className="w-8 h-8" />
            </div>

            <div className="max-w-md">
              <h3 className="text-lg font-bold text-white mb-1.5">
                Vincule sua conta Strava
              </h3>
              <p className="text-sm text-slate-400">
                Para sincronizar seus tempos e ritmos oficiais, conecte sua conta
                Strava com 1 clique.
              </p>
            </div>

            <StravaConnectButton
              onClick={handleConnect}
              loading={isConnecting}
              className="mt-2"
            />
          </div>
        ) : (
          /* Caso 3: Conectado - Lista de Atividades */
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Sparkles className="w-4 h-4 text-[#FC4C02]" />
                <span>Selecione a atividade correspondente a esta corrida:</span>
              </div>

              <button
                type="button"
                onClick={() => fetchActivities()}
                disabled={activitiesLoading}
                className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors cursor-pointer disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-3.5 h-3.5 ${activitiesLoading ? "animate-spin" : ""}`}
                />
                Atualizar lista
              </button>
            </div>

            {successMessage && (
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                <span>{successMessage}</span>
              </div>
            )}

            {activitiesError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{activitiesError}</span>
              </div>
            )}

            {activitiesLoading ? (
              <div className="py-12 flex flex-col items-center justify-center gap-3 text-slate-400">
                <Spinner size="md" />
                <p className="text-sm">Buscando atividades recentes no Strava...</p>
              </div>
            ) : activities.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                Nenhuma atividade de corrida recente encontrada no seu Strava.
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto pr-1 space-y-2.5">
                {activities.map((act) => (
                  <StravaActivityCard
                    key={act.id}
                    activity={act}
                    isSelected={selectedActivity?.id === act.id}
                    onSelect={(activity) => setSelectedActivity(activity)}
                  />
                ))}
              </div>
            )}

            {/* Aviso de sobrescrita e Ações */}
            {selectedActivity && (
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 flex items-start gap-2.5">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Ao confirmar, os dados desta atividade (distância, tempo, pace
                  e velocidade) serão associados a esta prova, sobrescrevendo
                  métricas anteriores.
                </span>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={importing}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmImport}
                disabled={!selectedActivity || importing}
                loading={importing}
                className="bg-[#FC4C02] hover:bg-[#E34402] border-[#FC4C02]"
              >
                Confirmar e Importar Métricas
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default StravaImportModal;
